// =============================================================================
// DOCUMENTS SERVICE - Firebase Storage for Goal/Milestone Documents
// =============================================================================

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../../firebase';
import { AttachedDocument } from '../types';

// Allowed file types
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

// Max file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Upload a document for a milestone
 */
export async function uploadMilestoneDocument(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  file: File
): Promise<AttachedDocument> {
  // Validate file
  validateFile(file);

  // Generate unique path
  const timestamp = Date.now();
  const safeName = sanitizeFileName(file.name);
  const path = `goals/${ownerId}/${goalId}/milestones/${milestoneId}/${timestamp}_${safeName}`;
  
  // Upload to Firebase Storage
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  
  // Get download URL
  const url = await getDownloadURL(storageRef);
  
  return {
    id: `${milestoneId}_${timestamp}`,
    name: file.name,
    url,
    size: file.size,
    type: file.type,
    uploadedAt: timestamp,
  };
}

/**
 * Upload a document for a goal
 */
export async function uploadGoalDocument(
  ownerId: string,
  goalId: string,
  file: File
): Promise<AttachedDocument> {
  // Validate file
  validateFile(file);

  // Generate unique path
  const timestamp = Date.now();
  const safeName = sanitizeFileName(file.name);
  const path = `goals/${ownerId}/${goalId}/documents/${timestamp}_${safeName}`;
  
  // Upload to Firebase Storage
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  
  // Get download URL
  const url = await getDownloadURL(storageRef);
  
  return {
    id: `${goalId}_${timestamp}`,
    name: file.name,
    url,
    size: file.size,
    type: file.type,
    uploadedAt: timestamp,
  };
}

/**
 * Delete a document from storage
 */
export async function deleteDocument(documentUrl: string): Promise<void> {
  try {
    const storageRef = ref(storage, documentUrl);
    await deleteObject(storageRef);
  } catch (error) {
    // Ignore if file doesn't exist
    console.warn('Failed to delete document:', error);
  }
}

/**
 * Validate file before upload
 */
function validateFile(file: File): void {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`Bestand te groot. Maximum is ${formatFileSize(MAX_FILE_SIZE)}.`);
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Dit bestandstype is niet toegestaan. Toegestaan: PDF, Word, Excel, PowerPoint, afbeeldingen, tekst.');
  }
}

/**
 * Sanitize filename for storage
 */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/_+/g, '_')
    .substring(0, 100);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file icon based on MIME type
 */
export function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '[IMG]';
  if (mimeType === 'application/pdf') return '[PDF]';
  if (mimeType.includes('word') || mimeType.includes('document')) return '[DOC]';
  if (mimeType.includes('excel') || mimeType.includes('sheet')) return '[XLS]';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '[PPT]';
  if (mimeType.startsWith('text/')) return '[TXT]';
  return '[FILE]';
}
